/* eslint-disable @typescript-eslint/camelcase */ // i codici arrivano dal backend in snake_case
import moment from 'moment';
import {
  RuleConditionDefinition,
  RuleCustomValue,
  RuleEmailDelivery,
  RuleExecution,
  RuleExecutionOutcome,
  RuleLogCondition,
  RuleLogNode,
} from '@/types';
import { hydrateCondition, isCustomValueDisplayable } from '@/helpers/ruleHelpers';

// Testi del log delle esecuzioni delle regole (pagina /r/esecuzioni)

export const OUTCOME_LABELS: Record<RuleExecutionOutcome, string> = {
  triggered: 'Scattata',
  not_triggered: 'Non scattata',
  error: 'Errore',
};

export const OUTCOME_COLORS: Record<RuleExecutionOutcome, string> = {
  triggered: 'statusDone',
  not_triggered: 'grey lighten-2',
  error: 'error',
};

export const BLOCK_LABELS: Record<string, string> = {
  and: 'Blocco E',
  or: 'Blocco Oppure',
  not: 'Blocco Non',
};

// Perché una persona non ha ricevuto la mail (motivi registrati da Action.trigger)
export const EXCLUSION_LABELS: Record<string, string> = {
  virtual: 'utente virtuale',
  inactive: 'utente disattivato',
  missing_data: 'dati utente incompleti',
  current_user: 'è chi ha causato l\'evento',
  practice_assignee: 'assegnatario delle pratiche, escluso dall\'azione',
  assignment_assignee: 'assegnatario di un incarico, escluso dall\'azione',
  excluded_group: 'appartiene a un gruppo escluso dall\'azione',
  excluded_user: 'escluso dall\'azione',
  child_not_open: 'la lead figlia non è in lavorazione',
};

// Oltre questo tempo una consegna ancora in coda non avrà più un esito
const QUEUED_STALE_MS = 60 * 60 * 1000;

export function deliveryLabel(d: RuleEmailDelivery, now = Date.now()): string {
  switch (d.status) {
    case 'sent':
      return d.sentAt ? `inviata il ${moment(d.sentAt).format('DD/MM/YYYY')} alle ${moment(d.sentAt).format('HH:mm')}` : 'inviata';
    case 'log_only':
      return 'solo log: l\'invio delle email è spento in questo ambiente';
    case 'failed':
      return `rifiutata: ${d.error || 'errore sconosciuto'}`;
    case 'excluded':
      return `esclusa: ${EXCLUSION_LABELS[d.reason || ''] || d.reason || 'motivo sconosciuto'}`;
    case 'queued':
    default:
      return now - new Date(d.createdAt).getTime() > QUEUED_STALE_MS ? 'esito non registrato' : 'in coda';
  }
}

export function leaves(nodes: RuleLogNode[]): RuleLogCondition[] {
  return nodes.reduce<RuleLogCondition[]>(
    (acc, n) => (n.type === 'block' ? acc.concat(leaves(n.children)) : acc.concat([n])),
    [],
  );
}

export function notEvaluatedCount(item: RuleExecution): number {
  if (item.conditionsTotal === null || item.conditionsTotal === undefined) return 0;
  return Math.max(0, item.conditionsTotal - leaves(item.conditions || []).length);
}

// Il nodo di primo livello che ha fermato una regola "tutte": l'ultimo
// valutato con esito falso. Per una regola "almeno una" non c'è un nodo
// solo: sono tutte false.
export function stoppingNode(item: RuleExecution): RuleLogNode | null {
  if (item.outcome !== 'not_triggered' || item.ruleWhen !== 'all') return null;
  const nodes = item.conditions || [];
  for (let i = nodes.length - 1; i >= 0; i -= 1) {
    if (nodes[i].result === false) return nodes[i];
  }
  return null;
}

export function nodeLabel(node: RuleLogNode, definitions: RuleConditionDefinition[]): string {
  if (node.type === 'block') return BLOCK_LABELS[node.operator] || `Blocco ${node.operator}`;
  const definition = definitions.find((d) => d.codename === node.codename);
  return definition ? definition.name : node.codename;
}

export function conditionValues(node: RuleLogCondition, definitions: RuleConditionDefinition[]): RuleCustomValue[] {
  const hydrated = hydrateCondition({ codename: node.codename, name: '', customValues: node.customValues || [] }, definitions);
  return hydrated ? hydrated.customValues.filter(isCustomValueDisplayable) : [];
}
